import type { AppViewState } from "../app-view-state.ts";
import type { SkillStatusReport } from "../types.ts";
import { loadAgentSkills } from "./agent-skills.ts";
import {
  buildAgentEditorDraft,
  buildFleetGraphConfigDraft,
  deleteFleetGraphAgent,
  fetchFleetGraphConfig,
  listFleetGraphAgentIds,
  listFleetGraphBindings,
  newFleetBindingDraft,
  resolveFleetEffectiveModelLabel,
  saveFleetGraphAgent,
  saveFleetGraphBehaviorFiles,
  saveFleetGraphBindings,
  type FleetGraphBindingDraft,
  type FleetGraphConfigDraft,
} from "./fleet-graph-config.ts";

export type FleetGraphEditorMode = "create" | "agent" | "inbound";
export type FleetGraphEditorTab = "topology" | "config" | "behavior";

export type FleetGraphAgentDraft = {
  id: string;
  name: string;
  workspace: string;
  setDefault: boolean;
  allowAll: boolean;
  allowAgents: string[];
};

export type FleetGraphBehaviorDraft = {
  soul: string;
  agents: string;
  loaded: boolean;
};

export type FleetGraphEditorHost = AppViewState & {
  fleetGraphEditorOpen: boolean;
  fleetGraphEditorMode: FleetGraphEditorMode;
  fleetGraphEditorTab: FleetGraphEditorTab;
  fleetGraphEditorAgentId: string | null;
  fleetGraphEditorSaving: boolean;
  fleetGraphEditorError: string | null;
  fleetGraphEditorAgentDraft: FleetGraphAgentDraft;
  fleetGraphEditorConfigDraft: FleetGraphConfigDraft;
  fleetGraphEditorBehaviorDraft: FleetGraphBehaviorDraft;
  fleetGraphEditorBindings: FleetGraphBindingDraft[];
  fleetGraphEditorAgentIds: string[];
  fleetGraphEditorSkillsReport: SkillStatusReport | null;
  fleetGraphEditorEffectiveModel: string;
  fleetGraphEditorConfigSnapshot: Record<string, unknown> | null;
};

export const EMPTY_FLEET_GRAPH_AGENT_DRAFT: FleetGraphAgentDraft = {
  id: "",
  name: "",
  workspace: "",
  setDefault: false,
  allowAll: false,
  allowAgents: [],
};

export const EMPTY_FLEET_GRAPH_CONFIG_DRAFT: FleetGraphConfigDraft = {
  modelPrimary: null,
  toolsProfile: null,
  skillsAllowlist: null,
  identityName: "",
  identityEmoji: "",
  identityTheme: "",
};

export const EMPTY_FLEET_GRAPH_BEHAVIOR_DRAFT: FleetGraphBehaviorDraft = {
  soul: "",
  agents: "",
  loaded: false,
};

export function closeFleetGraphEditor(host: FleetGraphEditorHost): void {
  host.fleetGraphEditorOpen = false;
  host.fleetGraphEditorError = null;
  host.fleetGraphEditorAgentId = null;
  host.fleetGraphEditorTab = "topology";
}

async function loadBehaviorFiles(host: FleetGraphEditorHost, agentId: string): Promise<void> {
  if (!host.client || !host.connected) {
    host.fleetGraphEditorBehaviorDraft = { ...EMPTY_FLEET_GRAPH_BEHAVIOR_DRAFT };
    return;
  }
  const id = agentId.trim();
  const readFile = async (name: string): Promise<string> => {
    try {
      const res = await host.client!.request<{ file?: { content?: string } } | null>(
        "agents.files.get",
        { agentId: id, name },
      );
      return res?.file?.content ?? "";
    } catch {
      return "";
    }
  };
  const [soul, agents] = await Promise.all([readFile("SOUL.md"), readFile("AGENTS.md")]);
  host.fleetGraphEditorBehaviorDraft = { soul, agents, loaded: true };
}

export async function openFleetGraphCreateAgent(host: FleetGraphEditorHost): Promise<void> {
  host.fleetGraphEditorMode = "create";
  host.fleetGraphEditorTab = "topology";
  host.fleetGraphEditorAgentId = null;
  host.fleetGraphEditorError = null;
  host.fleetGraphEditorAgentDraft = { ...EMPTY_FLEET_GRAPH_AGENT_DRAFT };
  host.fleetGraphEditorConfigDraft = { ...EMPTY_FLEET_GRAPH_CONFIG_DRAFT };
  host.fleetGraphEditorBehaviorDraft = { ...EMPTY_FLEET_GRAPH_BEHAVIOR_DRAFT };
  host.fleetGraphEditorSkillsReport = null;
  host.fleetGraphEditorEffectiveModel = "—";
  try {
    const config = await fetchFleetGraphConfig(host);
    host.fleetGraphEditorAgentIds = listFleetGraphAgentIds(config);
  } catch (err) {
    host.fleetGraphEditorError = String(err);
    host.fleetGraphEditorAgentIds = [];
  }
  host.fleetGraphEditorOpen = true;
}

export async function openFleetGraphAgentEditor(
  host: FleetGraphEditorHost,
  agentId: string,
): Promise<void> {
  host.fleetGraphEditorMode = "agent";
  host.fleetGraphEditorTab = "topology";
  host.fleetGraphEditorAgentId = agentId;
  host.fleetGraphEditorError = null;
  try {
    const config = await fetchFleetGraphConfig(host);
    const ids = listFleetGraphAgentIds(config);
    host.fleetGraphEditorAgentIds = ids;
    host.fleetGraphEditorAgentDraft = buildAgentEditorDraft(config, agentId, ids);
    host.fleetGraphEditorConfigDraft = buildFleetGraphConfigDraft(config, agentId);
    host.fleetGraphEditorConfigSnapshot = config;
    host.fleetGraphEditorEffectiveModel = resolveFleetEffectiveModelLabel(config, agentId);
    await Promise.all([
      loadBehaviorFiles(host, agentId),
      loadAgentSkills(host, agentId).then(() => {
        host.fleetGraphEditorSkillsReport = host.agentSkillsReport;
      }),
    ]);
  } catch (err) {
    host.fleetGraphEditorError = String(err);
    host.fleetGraphEditorAgentDraft = { ...EMPTY_FLEET_GRAPH_AGENT_DRAFT, id: agentId };
    host.fleetGraphEditorAgentIds = [];
  }
  host.fleetGraphEditorOpen = true;
}

export async function openFleetGraphInboundEditor(host: FleetGraphEditorHost): Promise<void> {
  host.fleetGraphEditorMode = "inbound";
  host.fleetGraphEditorTab = "topology";
  host.fleetGraphEditorAgentId = null;
  host.fleetGraphEditorError = null;
  try {
    const config = await fetchFleetGraphConfig(host);
    host.fleetGraphEditorAgentIds = listFleetGraphAgentIds(config);
    host.fleetGraphEditorBindings = listFleetGraphBindings(config);
  } catch (err) {
    host.fleetGraphEditorError = String(err);
    host.fleetGraphEditorBindings = [];
    host.fleetGraphEditorAgentIds = [];
  }
  host.fleetGraphEditorOpen = true;
}

export function setFleetGraphEditorTab(host: FleetGraphEditorHost, tab: FleetGraphEditorTab): void {
  host.fleetGraphEditorTab = tab;
}

export function patchFleetGraphAgentDraft(
  host: FleetGraphEditorHost,
  patch: Partial<FleetGraphAgentDraft>,
): void {
  host.fleetGraphEditorAgentDraft = { ...host.fleetGraphEditorAgentDraft, ...patch };
}

export function patchFleetGraphConfigDraft(
  host: FleetGraphEditorHost,
  patch: Partial<FleetGraphConfigDraft>,
): void {
  host.fleetGraphEditorConfigDraft = { ...host.fleetGraphEditorConfigDraft, ...patch };
}

export function patchFleetGraphBehaviorDraft(
  host: FleetGraphEditorHost,
  patch: Partial<FleetGraphBehaviorDraft>,
): void {
  host.fleetGraphEditorBehaviorDraft = { ...host.fleetGraphEditorBehaviorDraft, ...patch };
}

export function toggleFleetGraphAllowAgent(host: FleetGraphEditorHost, targetId: string): void {
  const draft = host.fleetGraphEditorAgentDraft;
  if (draft.allowAll) {
    return;
  }
  const id = targetId.trim();
  const next = new Set(draft.allowAgents);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  host.fleetGraphEditorAgentDraft = { ...draft, allowAgents: [...next] };
}

export function toggleFleetGraphSkill(host: FleetGraphEditorHost, skillName: string): void {
  const draft = host.fleetGraphEditorConfigDraft;
  const allSkills =
    host.fleetGraphEditorSkillsReport?.skills?.map((s) => s.name).filter(Boolean) ?? [];
  const current = draft.skillsAllowlist ?? [...allSkills];
  const next = new Set(current);
  if (next.has(skillName)) {
    next.delete(skillName);
  } else {
    next.add(skillName);
  }
  host.fleetGraphEditorConfigDraft = {
    ...draft,
    skillsAllowlist: [...next],
  };
}

export function setFleetGraphSkillsAllowAll(host: FleetGraphEditorHost, allowAll: boolean): void {
  host.fleetGraphEditorConfigDraft = {
    ...host.fleetGraphEditorConfigDraft,
    skillsAllowlist: allowAll ? null : [],
  };
}

export function patchFleetGraphBinding(
  host: FleetGraphEditorHost,
  index: number,
  patch: Partial<FleetGraphBindingDraft>,
): void {
  const rows = [...host.fleetGraphEditorBindings];
  const row = rows[index];
  if (!row) {
    return;
  }
  rows[index] = { ...row, ...patch };
  host.fleetGraphEditorBindings = rows;
}

export function addFleetGraphBindingRow(host: FleetGraphEditorHost, agentId = ""): void {
  host.fleetGraphEditorBindings = [...host.fleetGraphEditorBindings, newFleetBindingDraft(agentId)];
}

export function removeFleetGraphBindingRow(host: FleetGraphEditorHost, index: number): void {
  host.fleetGraphEditorBindings = host.fleetGraphEditorBindings.filter((_, i) => i !== index);
}

export async function submitFleetGraphEditor(host: FleetGraphEditorHost): Promise<void> {
  try {
    if (host.fleetGraphEditorMode === "inbound") {
      await saveFleetGraphBindings(host, host.fleetGraphEditorBindings);
    } else {
      const agentId = host.fleetGraphEditorAgentDraft.id.trim();
      await saveFleetGraphAgent(host, {
        mode: host.fleetGraphEditorMode === "create" ? "create" : "edit",
        ...host.fleetGraphEditorAgentDraft,
        config: host.fleetGraphEditorConfigDraft,
      });
      if (
        host.fleetGraphEditorMode === "agent" &&
        agentId &&
        host.fleetGraphEditorBehaviorDraft.loaded
      ) {
        await saveFleetGraphBehaviorFiles(host, agentId, {
          soul: host.fleetGraphEditorBehaviorDraft.soul,
          agents: host.fleetGraphEditorBehaviorDraft.agents,
        });
      }
    }
    closeFleetGraphEditor(host);
  } catch {
    /* error on host */
  }
}

export async function saveFleetGraphBehaviorTab(host: FleetGraphEditorHost): Promise<void> {
  const agentId = host.fleetGraphEditorAgentId ?? host.fleetGraphEditorAgentDraft.id;
  if (!agentId.trim()) {
    return;
  }
  host.fleetGraphEditorSaving = true;
  host.fleetGraphEditorError = null;
  try {
    await saveFleetGraphBehaviorFiles(host, agentId, {
      soul: host.fleetGraphEditorBehaviorDraft.soul,
      agents: host.fleetGraphEditorBehaviorDraft.agents,
    });
  } catch (err) {
    host.fleetGraphEditorError = String(err);
  } finally {
    host.fleetGraphEditorSaving = false;
  }
}

export async function deleteFleetGraphAgentFromEditor(host: FleetGraphEditorHost): Promise<void> {
  const id = host.fleetGraphEditorAgentId ?? host.fleetGraphEditorAgentDraft.id;
  if (!id) {
    return;
  }
  try {
    await deleteFleetGraphAgent(host, id);
    closeFleetGraphEditor(host);
  } catch {
    /* error on host */
  }
}
